export enum VaccinationScheduleDisease {
  IPV = "ipv",
  VITA = "vit-a",
  MEASLES = "measles",
  YELLOWFEVER = "yellow-fever",
  MENINGITIS = "meningitis",
  HPV = "hpv",
  BCG = "bcg",
  OPV = "opv",
  TETANUS = "tetanus",
  HEPB = "hepatitis-b",
  DIPHTHERIA = "diphtheria",
  HIB = "hib",
  PERTUSSIS = "pertussis",
  PCV = "pcv",
  ROTAVIRUS = "rotavirus",
}

export enum TimeUnit {
  WEEKS = "WEEKS",
  MONTHS = "MONTHS",
  YEARS = "YEARS",
}

export type VaccineCode = keyof typeof allVaccineCodesToDetailsMap;
export type VaccinationScheduleDose = {
  codes: VaccineCode[];
  /**
   * some vaccines must be given X weeks after the previous dose, others are from DOB
   */
  calculateFromPrevDose: boolean;
  /**
   * the amount of weeks, months or years after the previous dose (or DOB) the vaccine is due
   */
  scheduledAfterPreviousQuantity: number;
  scheduledAfterPreviousTimeUnit: TimeUnit;
  /**
   * the amount of weeks, months or years after the DOB the vaccine is considered to be missed
   */
  missingAfterDobQuantity: number;
  missingAfterDobTimeUnit: TimeUnit;
  /**
   * when vaccine was given to the patient
   */
  completedDate: Date | null;
  /**
   * for remaining vaccines, when it should be considered due or missing
   */
  dueDate: Date | null;
  /**
   * the amount of weeks, months or years after the DOB the vaccine is considered to be missed
   */
  missingDate: Date | null;
};
export type VaccinationSchedule = {
  [disease in VaccinationScheduleDisease]: {
    displayName: string;
    // much logic depends on the doses being correctly ordered from earliest to latest scheduled
    doses: VaccinationScheduleDose[];
  };
};

/**
 * all immunisation diseases in the EOS DB, even those not in the schedule
 */
export const allVaccineCodesToDetailsMap: Record<
  string,
  {
    displayName: string;
    keywords: string[];
    /**
     * some vaccines are intended to be given in a specific order, mainly
     * those that are childhood vaccines. E.g. Penta 1, 2, 3 are given in that order.
     * These are the codes of the previous vaccines in the sequence.
     */
    previousVaccineCodes?: Array<string>;
  }
> = {
  $IMM1: {
    displayName: "BCG",
    keywords: [],
  },
  $IMM10: {
    displayName: "MR 1",
    keywords: [],
  },
  $IMM26: {
    displayName: "MR 2",
    keywords: [],
    previousVaccineCodes: ["$IMM10"],
  },
  $IMM11: {
    displayName: "Tetanus",
    keywords: [],
  },
  $IMM12: {
    displayName: "HPV 1",
    keywords: [],
  },
  $IMM27: {
    displayName: "HPV 2",
    keywords: [],
    previousVaccineCodes: ["$IMM12"],
  },
  $IMM13: {
    displayName: "Penta 1",
    keywords: [],
  },
  $IMM28: {
    displayName: "Penta 2",
    keywords: [],
    previousVaccineCodes: ["$IMM13"],
  },
  $IMM29: {
    displayName: "Penta 3",
    keywords: [],
    previousVaccineCodes: ["$IMM28", "$IMM13"],
  },
  $IMM14: {
    displayName: "IPV 1",
    keywords: [],
  },
  $IMM30: {
    displayName: "IPV 2",
    keywords: [],
    previousVaccineCodes: ["$IMM14"],
  },
  $IMM16: {
    displayName: "MMR",
    keywords: [],
  },
  $IMM2: {
    displayName: "OPV 0",
    keywords: [],
  },
  $IMM17: {
    displayName: "OPV 1",
    keywords: [],
    previousVaccineCodes: ["$IMM2"],
  },
  $IMM18: {
    displayName: "OPV 2",
    keywords: [],
    previousVaccineCodes: ["$IMM17", "$IMM2"],
  },
  $IMM19: {
    displayName: "OPV 3",
    keywords: [],
    previousVaccineCodes: ["$IMM18", "$IMM17", "$IMM2"],
  },
  $IMM6: {
    displayName: "DPT-HIB-HepB 1",
    keywords: [],
  },
  $IMM20: {
    displayName: "DPT-HIB-HepB 2",
    keywords: [],
    previousVaccineCodes: ["$IMM6"],
  },
  $IMM21: {
    displayName: "DPT-HIB-HepB 3",
    keywords: [],
    previousVaccineCodes: ["$IMM20", "$IMM6"],
  },
  $IMM7: {
    displayName: "Pneumoccocal 1",
    keywords: [],
  },
  $IMM22: {
    displayName: "Pneumoccocal 2",
    keywords: [],
    previousVaccineCodes: ["$IMM7"],
  },
  $IMM23: {
    displayName: "Pneumoccocal 3",
    keywords: [],
    previousVaccineCodes: ["$IMM22", "$IMM7"],
  },
  $IMM8: {
    displayName: "Rotavirus 1",
    keywords: [],
  },
  $IMM24: {
    displayName: "Rotavirus 2",
    keywords: [],
    previousVaccineCodes: ["$IMM8"],
  },
  $IMM25: {
    displayName: "Rotavirus 3",
    keywords: [],
    previousVaccineCodes: ["$IMM24", "$IMM8"],
  },
  $IMM3: {
    displayName: "DPT",
    keywords: [],
  },
  $IMM15: {
    displayName: "Measles 1",
    keywords: [],
  },
  $IMM31: {
    displayName: "Measles 2",
    keywords: [],
    previousVaccineCodes: ["$IMM15"],
  },
  $IMM32: {
    displayName: "Meningitis A",
    keywords: [],
  },
  $IMM33: {
    displayName: "Vitamin A 1",
    keywords: [],
  },
  $IMM34: {
    displayName: "Vitamin A 2",
    keywords: [],
    previousVaccineCodes: ["$IMM33"],
  },
  $IMM39: {
    displayName: "Vitamin A 3",
    keywords: [],
    previousVaccineCodes: ["$IMM34", "$IMM33"],
  },
  $IMM40: {
    displayName: "Vitamin A 4",
    keywords: [],
    previousVaccineCodes: ["$IMM39", "$IMM34", "$IMM33"],
  },
  $IMM35: {
    displayName: "Malaria",
    keywords: [],
  },
  $IMM36: {
    displayName: "COVID-19",
    keywords: [],
  },
  $IMM37: {
    displayName: "Typhoid polysaccharide",
    keywords: [],
  },
  $IMM38: {
    displayName: "TD",
    keywords: [],
  },
  $IMM4: {
    displayName: "HIB",
    keywords: [],
  },
  $IMM5: {
    displayName: "HBV",
    keywords: ["hep", "hepatitis", "hep b"],
  },
  $IMM9: {
    displayName: "Yellow Fever",
    keywords: [],
  },
} as const;

/**
 * based on https://elephant.slab.com/posts/nigeria-childhood-immunisation-schedule-vgb0niht
 */
export const vaccinationScheduleTemplate: VaccinationSchedule = {
  [VaccinationScheduleDisease.IPV]: {
    displayName: "IPV",
    doses: [
      {
        codes: ["$IMM14"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM30"],
        scheduledAfterPreviousQuantity: 8,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.VITA]: {
    displayName: "Vitamin A",
    doses: [
      {
        codes: ["$IMM33"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM34"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.MEASLES]: {
    displayName: "Measles",
    doses: [
      {
        codes: ["$IMM15"],
        scheduledAfterPreviousQuantity: 9,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM31"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.YELLOWFEVER]: {
    displayName: "Yellow Fever",
    doses: [
      {
        codes: ["$IMM9"],
        scheduledAfterPreviousQuantity: 9,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.MENINGITIS]: {
    displayName: "Meningitis",
    doses: [
      {
        codes: ["$IMM32"],
        scheduledAfterPreviousQuantity: 12,
        scheduledAfterPreviousTimeUnit: TimeUnit.MONTHS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.HPV]: {
    displayName: "HPV",
    doses: [
      {
        codes: ["$IMM12"],
        scheduledAfterPreviousQuantity: 9,
        scheduledAfterPreviousTimeUnit: TimeUnit.YEARS,
        missingAfterDobQuantity: 10,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.BCG]: {
    displayName: "BCG",
    doses: [
      {
        codes: ["$IMM1"],
        scheduledAfterPreviousQuantity: 0,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 2,
        missingAfterDobTimeUnit: TimeUnit.YEARS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.OPV]: {
    displayName: "OPV",
    doses: [
      {
        codes: ["$IMM2"],
        scheduledAfterPreviousQuantity: 0,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM17"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM18"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM19"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.PERTUSSIS]: {
    displayName: "Pertussis",
    doses: [
      {
        codes: ["$IMM13", "$IMM6", "$IMM3"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM28", "$IMM20", "$IMM3"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM29", "$IMM21", "$IMM3"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.DIPHTHERIA]: {
    displayName: "Diphtheria",
    doses: [
      {
        codes: ["$IMM13", "$IMM6", "$IMM3", "$IMM38"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM28", "$IMM20", "$IMM3", "$IMM38"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM29", "$IMM21", "$IMM3", "$IMM38"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.TETANUS]: {
    displayName: "Tetanus",
    doses: [
      {
        codes: ["$IMM13", "$IMM6", "$IMM3", "$IMM38", "$IMM11"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM28", "$IMM20", "$IMM3", "$IMM38", "$IMM11"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM29", "$IMM21", "$IMM3", "$IMM38", "$IMM11"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.HEPB]: {
    displayName: "Hepatitis B",
    doses: [
      {
        codes: ["$IMM5"],
        scheduledAfterPreviousQuantity: 0,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM13", "$IMM6", "$IMM5"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM28", "$IMM20", "$IMM5"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM29", "$IMM21", "$IMM5"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.HIB]: {
    displayName: "HIB",
    doses: [
      {
        codes: ["$IMM13", "$IMM6", "$IMM4"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM28", "$IMM20", "$IMM4"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM29", "$IMM21", "$IMM4"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.PCV]: {
    displayName: "Pneumoccocal (PCV)",
    doses: [
      {
        codes: ["$IMM7"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM22"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM23"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
  [VaccinationScheduleDisease.ROTAVIRUS]: {
    displayName: "Rotavirus",
    doses: [
      {
        codes: ["$IMM8"],
        scheduledAfterPreviousQuantity: 6,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: false,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM24"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
      {
        codes: ["$IMM25"],
        scheduledAfterPreviousQuantity: 4,
        scheduledAfterPreviousTimeUnit: TimeUnit.WEEKS,
        missingAfterDobQuantity: 6,
        missingAfterDobTimeUnit: TimeUnit.MONTHS,
        calculateFromPrevDose: true,
        completedDate: null,
        dueDate: null,
        missingDate: null,
      },
    ],
  },
};
